import { ChevronDown, ChevronUp } from 'lucide-react'
import { useId, useState } from 'react'
import { Card, CardHeader } from '@/components/ui/Card'
import { Select } from '@/components/ui/Select'
import type { RecurringFrequency } from '@/lib/recurring-types'

export const FREQ_LABEL: Record<RecurringFrequency, string> = {
  ONCE: 'Eenmalig',
  MONTHLY: 'Maandelijks',
  QUARTERLY: 'Per kwartaal',
  YEARLY: 'Jaarlijks',
}
const FREQ_OPTIONS: RecurringFrequency[] = ['ONCE', 'MONTHLY', 'QUARTERLY', 'YEARLY']
export const MONTHS = [
  'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
  'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December',
]

export interface ScheduleForm {
  name: string
  subjectTemplate: string
  frequency: RecurringFrequency
  intervalCount: string
  nextIssueDate: string
  leadTimeDays: string
  endDate: string
  indexed: boolean
  indexationMonth: string
}

function Label({ children, id }: { children: string; id?: string }) {
  return <span id={id} className="mb-1.5 block text-sm font-medium text-ink-soft">{children}</span>
}

function formatDate(value: string): string {
  if (!value) return '-'
  const [year, month, day] = value.split('-')
  return day && month && year ? `${day}-${month}-${year}` : value
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <div className="lg:px-4 lg:py-2.5 lg:first:pl-0">
      <span className="label-caps block">{label}</span>
      <span className="mt-0.5 block text-sm text-ink">{value}</span>
    </div>
  )
}

/**
 * Het schema is bij het bewerken van de inhoud zelden aan de orde, maar je wilt
 * de feiten wel zien. Ingeklapt is het daarom een leesstrip; uitklappen geeft de
 * velden.
 */
export function ScheduleCard({
  schedule,
  onChange,
  readonly,
  periodLabel,
}: {
  schedule: ScheduleForm
  onChange: (patch: Partial<ScheduleForm>) => void
  readonly: boolean
  periodLabel: string | null
}) {
  const incomplete = schedule.name.trim() === '' || schedule.subjectTemplate.trim() === '' || schedule.nextIssueDate === ''
  const [open, setOpen] = useState(incomplete)
  const frequencyLabelId = useId()
  const monthLabelId = useId()

  const frequency = `${FREQ_LABEL[schedule.frequency]}${Number(schedule.intervalCount) > 1 ? ` x${schedule.intervalCount}` : ''}`
  const indexation = schedule.indexed ? `Jaarlijks in ${MONTHS[Number(schedule.indexationMonth) - 1] ?? '-'}` : 'Geen'

  return (
    <Card>
      <CardHeader
        title="Schema"
        subtitle="Wanneer deze reeks een concept oplevert"
        action={
          <button type="button" className="btn-ghost h-8 px-2.5 text-sm" onClick={() => setOpen((v) => !v)}>
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {readonly ? (open ? 'Verbergen' : 'Tonen') : open ? 'Sluiten' : 'Wijzigen'}
          </button>
        }
      />

      {open ? (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <label className="block sm:col-span-2 lg:col-span-1">
            <Label>Naam</Label>
            <input className="field" value={schedule.name} onChange={(e) => onChange({ name: e.target.value })} disabled={readonly} />
          </label>
          <label className="block sm:col-span-2">
            <Label>Onderwerp-sjabloon</Label>
            <input
              className="field"
              value={schedule.subjectTemplate}
              onChange={(e) => onChange({ subjectTemplate: e.target.value })}
              placeholder="Licentie {{jaar}}"
              disabled={readonly}
            />
          </label>
          <div className="block">
            <Label id={frequencyLabelId}>Frequentie</Label>
            <Select
              value={schedule.frequency}
              onValueChange={(v) => onChange({ frequency: v as RecurringFrequency })}
              options={FREQ_OPTIONS.map((freq) => ({ value: freq, label: FREQ_LABEL[freq] }))}
              ariaLabelledby={frequencyLabelId}
              className="w-full"
              disabled={readonly}
            />
          </div>
          <label className="block">
            <Label>Interval</Label>
            <input
              className="field num"
              type="number"
              min={1}
              value={schedule.intervalCount}
              onChange={(e) => onChange({ intervalCount: e.target.value })}
              disabled={readonly}
            />
          </label>
          <label className="block">
            <Label>Volgende factuur</Label>
            <input
              className="field"
              type="date"
              value={schedule.nextIssueDate}
              onChange={(e) => onChange({ nextIssueDate: e.target.value })}
              disabled={readonly}
            />
          </label>
          <label className="block">
            <Label>Voorlooptijd (dagen)</Label>
            <input
              className="field num"
              type="number"
              min={0}
              value={schedule.leadTimeDays}
              onChange={(e) => onChange({ leadTimeDays: e.target.value })}
              disabled={readonly}
            />
          </label>
          <label className="block">
            <Label>Einddatum (optioneel)</Label>
            <input
              className="field"
              type="date"
              value={schedule.endDate}
              onChange={(e) => onChange({ endDate: e.target.value })}
              disabled={readonly}
            />
          </label>
          <div className="flex items-end gap-3">
            <label className="flex items-center gap-2.5 pb-2">
              <input
                type="checkbox"
                className="h-4 w-4 accent-accent-600"
                checked={schedule.indexed}
                onChange={(e) => onChange({ indexed: e.target.checked })}
                disabled={readonly}
              />
              <span className="text-sm font-medium text-ink-soft">Jaarlijkse indexatie</span>
            </label>
            {schedule.indexed ? (
              <div className="block flex-1">
                <Label id={monthLabelId}>Indexatiemaand</Label>
                <Select
                  value={schedule.indexationMonth}
                  onValueChange={(v) => onChange({ indexationMonth: v })}
                  options={MONTHS.map((month, i) => ({ value: String(i + 1), label: month }))}
                  ariaLabelledby={monthLabelId}
                  className="w-full"
                  disabled={readonly}
                />
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:flex lg:flex-wrap lg:gap-0 lg:divide-x lg:divide-hairline">
          <Cell label="Frequentie" value={frequency} />
          <Cell label="Volgende factuur" value={formatDate(schedule.nextIssueDate)} />
          {periodLabel ? <Cell label="Periode" value={periodLabel} /> : null}
          <Cell label="Voorlooptijd" value={`${schedule.leadTimeDays || 0} dagen`} />
          <Cell label="Einddatum" value={schedule.endDate ? formatDate(schedule.endDate) : 'Geen'} />
          <Cell label="Indexatie" value={indexation} />
        </div>
      )}
    </Card>
  )
}
